import React, { useMemo, useState, useEffect } from 'react';
import {
    Button,
    Card,
    Col,
    Form,
    Modal,
    Row,
    Spinner,
    Badge,
    InputGroup,
    Collapse,
} from 'react-bootstrap';
import SmartSearchSelect from '../../../components/Smart/SmartSearchSelect';
import { useFormat } from '../../../components/FormatContext';
import { fromCents } from '../../../utils/money';
import useMotoSearch from '../hooks/useMotoSearch';

const MotoModelImage = ({ src, alt }) => {
    const [imgError, setImgError] = useState(false);

    if (!src || imgError) {
        return (
            <div
                className="d-flex align-items-center justify-content-center bg-light border rounded"
                style={{
                    width: 96,
                    height: 72,
                    fontSize: 12,
                    color: '#6c757d',
                    flexShrink: 0,
                }}
            >
                No image
            </div>
        );
    }

    return (
        <img
            src={src}
            alt={alt}
            onError={() => setImgError(true)}
            className="border rounded bg-light"
            style={{
                width: 96,
                height: 72,
                objectFit: 'cover',
                flexShrink: 0,
            }}
        />
    );
};

const formatMotoType = (type) => {
    if (!type) return '';

    const normalized = String(type).trim();

    const typeMap = {
        SCOOTER: 'Scooter',
        SPORT: 'Sport',
        SPORTS: 'Sport',
        NAKED: 'Naked',
        TOURING: 'Touring',
        ADVENTURE: 'Adventure',
        ADVENTURE_BIKE: 'Adventure',
        OFF_ROAD: 'Off-road',
        ENDURO: 'Enduro',
        CRUISER: 'Cruiser',
        CLASSIC: 'Classic',
        CUSTOM: 'Custom',
        ROADSTER: 'Roadster',
        SUPERMOTO: 'Supermoto',
        TRAIL: 'Trail',
        ELECTRIC: 'Electric',
        MOPED: 'Moped',
    };

    if (typeMap[normalized]) {
        return typeMap[normalized];
    }

    return normalized
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
};


const MotoSearchModal = ({
    show,
    onClose,
    onConfirm,
    request,
}) => {
    const { formatCurrency } = useFormat();

    const {
        form,
        countries,
        cities,
        locations,
        loadingCountries,
        loadingCities,
        loadingLocations,
        searchLoading,
        searchResult,
        errorMsg,
        updateField,
        handleCountryChange,
        handleCityChange,
        handleSearch,
    } = useMotoSearch({
        show,
        requestId: request?.id,
        request,
    });

    const [selectedBrands, setSelectedBrands] = useState([]);
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [expandedModels, setExpandedModels] = useState({});
    const [showSearchPanel, setShowSearchPanel] = useState(true);
    const [selectedShopMotorcycleId, setSelectedShopMotorcycleId] = useState('');

    useEffect(() => {
        if (!show) return;
        setSelectedBrands([]);
        setMinPrice('');
        setMaxPrice('');
        setExpandedModels({});
        setShowSearchPanel(true);
        setSelectedShopMotorcycleId('');
    }, [show]);

    useEffect(() => {
        setSelectedBrands([]);
        setMinPrice('');
        setMaxPrice('');
        setExpandedModels({});
        setSelectedShopMotorcycleId('');

        if (searchResult) {
            setShowSearchPanel(false);
        }
    }, [searchResult]);

    const handleConfirm = () => {
        onConfirm?.({
            form,
            selectedShopMotorcycleId: selectedShopMotorcycleId,
        });
    };

    const shops = searchResult?.shops || [];
    const totalShops = searchResult?.totalShops ?? shops.length;
    const totalMotos = searchResult?.totalMotos ?? 0;

    const groupedModels = useMemo(() => {
        const grouped = new Map();

        shops.forEach((shop) => {
            const motos = shop?.motos || [];

            motos.forEach((moto) => {
                const modelKey =
                    moto?.motorcycleModelId ||
                    `${moto?.brandName || ''}-${moto?.name || ''}` ||
                    moto?.shopMotorcycleId;

                if (!grouped.has(modelKey)) {
                    grouped.set(modelKey, {
                        modelKey,
                        motorcycleModelId: moto?.motorcycleModelId || '',
                        brandName: moto?.brandName || '',
                        modelName: moto?.name || 'Motorcycle',
                        slug: moto?.slug || '',
                        imageUrl: '',
                        offers: [],
                    });
                }

                const currentGroup = grouped.get(modelKey);

                // 优先补全 imageUrl，不要只靠第一条 moto
                if (!currentGroup.imageUrl && moto?.imageUrl) {
                    currentGroup.imageUrl = moto.imageUrl;
                }

                currentGroup.offers.push({
                    shopId: shop?.id,
                    shopName: shop?.name || 'Shop',
                    supplierName: shop?.supplier?.companyName || '—',
                    locationName: shop?.location?.name || '—',
                    cityName: shop?.location?.city || '—',
                    countryName: shop?.location?.country || '—',
                    dailyPrice: Number(moto?.dailyPrice || 0),
                    totalPrice: Number(moto?.totalPrice || 0),
                    bestOffer: !!moto?.bestOffer,
                    type: moto?.type || '',
                    shopMotorcycleId: moto?.shopMotorcycleId || '',
                    modelKey,
                });
            });
        });

        return Array.from(grouped.values())
            .map((group) => ({
                ...group,
                offers: [...group.offers].sort((a, b) => {
                    const totalDiff = Number(a.totalPrice || 0) - Number(b.totalPrice || 0);
                    if (totalDiff !== 0) return totalDiff;
                    return String(a.shopName || '').localeCompare(String(b.shopName || ''));
                }),
            }))
            .sort((a, b) => {
                const firstOfferA = a.offers?.[0]?.totalPrice ?? Number.MAX_SAFE_INTEGER;
                const firstOfferB = b.offers?.[0]?.totalPrice ?? Number.MAX_SAFE_INTEGER;
                if (firstOfferA !== firstOfferB) return firstOfferA - firstOfferB;

                const brandCmp = String(a.brandName || '').localeCompare(String(b.brandName || ''));
                if (brandCmp !== 0) return brandCmp;

                return String(a.modelName || '').localeCompare(String(b.modelName || ''));
            });
    }, [shops]);

    const availableBrands = useMemo(() => {
        const brandSet = new Set(
            groupedModels
                .map((item) => String(item.brandName || '').trim())
                .filter(Boolean),
        );

        return Array.from(brandSet)
            .sort((a, b) => a.localeCompare(b))
            .map((brand) => ({
                value: brand,
                label: brand,
            }));
    }, [groupedModels]);

    const allOfferPrices = useMemo(() => {
        return groupedModels.flatMap((group) =>
            (group.offers || []).map((offer) => Number(offer.totalPrice || 0)),
        );
    }, [groupedModels]);

    const detectedMinPrice = useMemo(() => {
        if (!allOfferPrices.length) return 0;
        return Math.min(...allOfferPrices);
    }, [allOfferPrices]);

    const detectedMaxPrice = useMemo(() => {
        if (!allOfferPrices.length) return 0;
        return Math.max(...allOfferPrices);
    }, [allOfferPrices]);

    const toCents = (value) => {
        if (value === '' || value == null) return null;
        const num = Number(value);
        if (!Number.isFinite(num)) return null;
        return Math.round(num * 100);
    };

    const filteredGroupedModels = useMemo(() => {
        const minValueCents = toCents(minPrice);
        const maxValueCents = toCents(maxPrice);

        return groupedModels
            .filter((group) => {
                if (selectedBrands.length === 0) return true;
                return selectedBrands.includes(group.brandName);
            })
            .map((group) => {
                const offers = (group.offers || []).filter((offer) => {
                    const priceCents = Number(offer.totalPrice || 0);

                    if (minValueCents !== null && priceCents < minValueCents) return false;
                    if (maxValueCents !== null && priceCents > maxValueCents) return false;

                    return true;
                });

                return {
                    ...group,
                    offers,
                };
            })
            .filter((group) => group.offers.length > 0)
            .sort((a, b) => {
                const firstOfferA = a.offers?.[0]?.totalPrice ?? Number.MAX_SAFE_INTEGER;
                const firstOfferB = b.offers?.[0]?.totalPrice ?? Number.MAX_SAFE_INTEGER;
                if (firstOfferA !== firstOfferB) return firstOfferA - firstOfferB;

                const brandCmp = String(a.brandName || '').localeCompare(String(b.brandName || ''));
                if (brandCmp !== 0) return brandCmp;

                return String(a.modelName || '').localeCompare(String(b.modelName || ''));
            });
    }, [groupedModels, selectedBrands, minPrice, maxPrice]);

    const hasActiveFilters = useMemo(() => {
        return selectedBrands.length > 0 || minPrice !== '' || maxPrice !== '';
    }, [selectedBrands, minPrice, maxPrice]);

    const displayGroupedModels = useMemo(() => {
        return hasActiveFilters ? filteredGroupedModels : groupedModels;
    }, [hasActiveFilters, filteredGroupedModels, groupedModels]);

    const displayOfferCount = useMemo(() => {
        return displayGroupedModels.reduce((sum, group) => sum + group.offers.length, 0);
    }, [displayGroupedModels]);

    const clearFilters = () => {
        setSelectedBrands([]);
        setMinPrice('');
        setMaxPrice('');
    };

    const toggleExpanded = (modelKey) => {
        setExpandedModels((prev) => ({
            ...prev,
            [modelKey]: !prev[modelKey],
        }));
    };

    const handleSelectOffer = (offer) => {
        setSelectedShopMotorcycleId(offer.shopMotorcycleId || '');
    };

    const searchSummary = useMemo(() => {
        const countryLabel =
            countries.find((x) => String(x.value) === String(form.countryId))?.label || 'Any country';
        const cityLabel =
            cities.find((x) => String(x.value) === String(form.cityId))?.label || 'Any city';
        const locationLabel =
            locations.find((x) => String(x.value) === String(form.locationId))?.label || 'Any location';

        return {
            countryLabel,
            cityLabel,
            locationLabel,
        };
    }, [countries, cities, locations, form.countryId, form.cityId, form.locationId]);

    const resultAreaHeight = showSearchPanel ? 'calc(100% - 210px)' : 'calc(100% - 64px)';

    return (
        <Modal
            show={show}
            onHide={onClose}
            size="xl"
            centered
            dialogClassName="modal-xl"
        >
            <Modal.Header closeButton>
                <Modal.Title>Generate moto offer</Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ height: '82vh', overflow: 'hidden' }}>
                {errorMsg && (
                    <div className="alert alert-danger py-2 mb-3">
                        {errorMsg}
                    </div>
                )}

                <Card className="mb-3">
                    <Card.Body className="py-2 px-3">
                        <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <div className="fw-semibold">Search</div>

                                {!!searchResult && (
                                    <div className="small text-muted d-flex gap-2 flex-wrap">
                                        <span>{searchSummary.countryLabel}</span>
                                        <span>·</span>
                                        <span>{searchSummary.cityLabel}</span>
                                        <span>·</span>
                                        <span>{searchSummary.locationLabel}</span>
                                        <span>·</span>
                                        <span>{form.startDate || '—'} {form.startTime || ''}</span>
                                        <span>→</span>
                                        <span>{form.endDate || '—'} {form.endTime || ''}</span>
                                    </div>
                                )}
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                {!!searchResult && (
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => setShowSearchPanel((prev) => !prev)}
                                    >
                                        {showSearchPanel ? 'Hide conditions' : 'Edit conditions'}
                                    </Button>
                                )}

                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={handleSearch}
                                    disabled={searchLoading || !form.startDate || !form.endDate}
                                >
                                    {searchLoading ? 'Searching...' : 'Search'}
                                </Button>
                            </div>
                        </div>

                        <Row className="g-2 mt-2">
                            {/* 第一行：Country / City / Location */}
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label className="small mb-1">Country</Form.Label>
                                    <SmartSearchSelect
                                        currentID={form.countryId}
                                        options={countries}
                                        onChange={(selected) => handleCountryChange(selected?.value || '')}
                                        placeholder={loadingCountries ? 'Loading countries...' : 'Select country'}
                                        noOptionsMessage="No countries found"
                                        loadingMessage="Searching countries..."
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label className="small mb-1">City</Form.Label>
                                    <SmartSearchSelect
                                        currentID={form.cityId}
                                        options={cities}
                                        onChange={(selected) => handleCityChange(selected?.value || '')}
                                        placeholder={
                                            !form.countryId
                                                ? 'Select country first'
                                                : loadingCities
                                                    ? 'Loading cities...'
                                                    : 'Select city'
                                        }
                                        noOptionsMessage="No cities found"
                                        loadingMessage="Searching cities..."
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label className="small mb-1">Location</Form.Label>
                                    <SmartSearchSelect
                                        currentID={form.locationId}
                                        options={locations}
                                        onChange={(selected) =>
                                            updateField('locationId', selected?.value || '')
                                        }
                                        placeholder={
                                            !form.cityId
                                                ? 'Select city first'
                                                : loadingLocations
                                                    ? 'Loading locations...'
                                                    : 'Any location'
                                        }
                                        noOptionsMessage="No locations found"
                                        loadingMessage="Searching locations..."
                                    />
                                </Form.Group>
                            </Col>

                            {/* 第二行：Start / End */}
                            <Col md={6}>
                                <Form.Label className="small mb-1">Start</Form.Label>

                                <InputGroup size="sm">
                                    <Form.Control
                                        type="date"
                                        value={form.startDate}
                                        onChange={(e) =>
                                            updateField('startDate', e.target.value)
                                        }
                                    />

                                    <Form.Control
                                        type="time"
                                        value={form.startTime}
                                        onChange={(e) =>
                                            updateField('startTime', e.target.value)
                                        }
                                        disabled={form.flexibleStartTime}
                                    />

                                    <InputGroup.Text className="bg-white">
                                        <Form.Check
                                            type="checkbox"
                                            id="flexibleStartTime"
                                            label="Flexible"
                                            checked={form.flexibleStartTime}
                                            onChange={(e) =>
                                                updateField(
                                                    'flexibleStartTime',
                                                    e.target.checked,
                                                )
                                            }
                                            className="mb-0 small"
                                        />
                                    </InputGroup.Text>
                                </InputGroup>
                            </Col>

                            <Col md={6}>
                                <Form.Label className="small mb-1">End</Form.Label>

                                <InputGroup size="sm">
                                    <Form.Control
                                        type="date"
                                        value={form.endDate}
                                        onChange={(e) =>
                                            updateField('endDate', e.target.value)
                                        }
                                    />

                                    <Form.Control
                                        type="time"
                                        value={form.endTime}
                                        onChange={(e) =>
                                            updateField('endTime', e.target.value)
                                        }
                                        disabled={form.flexibleEndTime}
                                    />

                                    <InputGroup.Text className="bg-white">
                                        <Form.Check
                                            type="checkbox"
                                            id="flexibleEndTime"
                                            label="Flexible"
                                            checked={form.flexibleEndTime}
                                            onChange={(e) =>
                                                updateField(
                                                    'flexibleEndTime',
                                                    e.target.checked,
                                                )
                                            }
                                            className="mb-0 small"
                                        />
                                    </InputGroup.Text>
                                </InputGroup>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                <Row className="g-3" style={{ height: resultAreaHeight }}>
                    <Col lg={9} style={{ height: '100%' }}>
                        <Card className="h-100">
                            <Card.Body className="p-3 d-flex flex-column" style={{ minHeight: 0 }}>
                                <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
                                    <div>
                                        <div className="fw-semibold mb-2">Search results</div>
                                        {!searchLoading && !!searchResult && (
                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                <Badge bg="light" text="dark" className="border">
                                                    {totalShops} shop{totalShops === 1 ? '' : 's'}
                                                </Badge>
                                                <Badge bg="light" text="dark" className="border">
                                                    {totalMotos} moto{totalMotos === 1 ? '' : 's'}
                                                </Badge>
                                                <Badge bg="light" text="dark" className="border">
                                                    {displayGroupedModels.length} model{displayGroupedModels.length === 1 ? '' : 's'}
                                                </Badge>
                                                <Badge bg="light" text="dark" className="border">
                                                    {displayOfferCount} offer{displayOfferCount === 1 ? '' : 's'}
                                                </Badge>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {searchLoading ? (
                                    <div className="text-muted d-flex align-items-center gap-2">
                                        <Spinner size="sm" /> Loading...
                                    </div>
                                ) : !searchResult ? (
                                    <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                                        Run a search to view moto offers.
                                    </div>
                                ) : displayGroupedModels.length === 0 ? (
                                    <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                                        No results found for the current filters.
                                    </div>
                                ) : (
                                    <div
                                        className="d-flex flex-column gap-3"
                                        style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 4 }}
                                    >
                                        {displayGroupedModels.map((group) => {
                                            const isExpanded = !!expandedModels[group.modelKey];
                                            const cheapestOffer = group.offers?.[0];
                                            const visibleOffers = isExpanded
                                                ? group.offers
                                                : cheapestOffer
                                                    ? [cheapestOffer]
                                                    : [];

                                            return (
                                                <Card key={group.modelKey} className="border">
                                                    <Card.Body className="p-3">
                                                        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
                                                            <div className="d-flex align-items-start gap-3">
                                                                <MotoModelImage
                                                                    src={group.imageUrl}
                                                                    alt={[group.brandName, group.modelName].filter(Boolean).join(' ')}
                                                                />

                                                                <div>
                                                                    <div className="fw-semibold fs-5 mb-1">
                                                                        {[group.brandName, group.modelName].filter(Boolean).join(' ')}
                                                                    </div>

                                                                    {!!cheapestOffer?.type && (
                                                                        <Badge bg="secondary" className="fw-normal">
                                                                            {formatMotoType(cheapestOffer.type)}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="d-flex align-items-center gap-2">
                                                                <Badge bg="light" text="dark" className="border">
                                                                    {group.offers.length} offer{group.offers.length === 1 ? '' : 's'}
                                                                </Badge>

                                                                {group.offers.length > 1 && (
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        size="sm"
                                                                        onClick={() => toggleExpanded(group.modelKey)}
                                                                    >
                                                                        {isExpanded ? 'Hide offers' : 'View offers'}
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="d-flex flex-column gap-2">
                                                            {visibleOffers.map((offer, index) => {
                                                                const isSelected = selectedShopMotorcycleId === offer.shopMotorcycleId;
                                                                return (
                                                                    <div
                                                                        key={offer.shopMotorcycleId || `${group.modelKey}-${offer.shopId}-${index}`}
                                                                        role="button"
                                                                        tabIndex={0}
                                                                        onClick={() => handleSelectOffer(offer)}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                                e.preventDefault();
                                                                                handleSelectOffer(offer);
                                                                            }
                                                                        }}
                                                                        className={`rounded p-3 ${isSelected ? 'border border-primary bg-light' : 'border'}`}
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                            transition: 'all 0.2s ease',
                                                                            boxShadow: isSelected ? '0 0 0 2px rgba(13,110,253,0.08)' : 'none',
                                                                        }}
                                                                    >
                                                                        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                                                                            <div className="d-flex align-items-start gap-3">
                                                                                <div
                                                                                    className={`rounded-circle d-flex align-items-center justify-content-center mt-1 ${isSelected ? 'border border-primary' : 'border'
                                                                                        }`}
                                                                                    style={{
                                                                                        width: 20,
                                                                                        height: 20,
                                                                                        minWidth: 20,
                                                                                        background: isSelected ? '#0d6efd' : '#fff',
                                                                                        color: '#fff',
                                                                                        fontSize: 12,
                                                                                        lineHeight: 1,
                                                                                    }}
                                                                                >
                                                                                    {isSelected ? '✓' : ''}
                                                                                </div>

                                                                                <div>
                                                                                    <div title={offer.shopName || ''}>
                                                                                        {offer.shopName || 'Shop'}
                                                                                    </div>

                                                                                    {offer.supplierName && offer.supplierName !== '—' && (
                                                                                        <div className="small text-muted">
                                                                                            {offer.supplierName}
                                                                                        </div>
                                                                                    )}

                                                                                    <div className="small text-muted">
                                                                                        {offer.locationName}
                                                                                        {' · '}
                                                                                        {offer.cityName}
                                                                                        {' · '}
                                                                                        {offer.countryName}
                                                                                    </div>

                                                                                    {offer.rentalDays != null && (
                                                                                        <div className="small text-muted">
                                                                                            {offer.rentalDays} rental day{offer.rentalDays === 1 ? '' : 's'}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </div>

                                                                            <div className="text-end">
                                                                                <div className="fw-semibold">
                                                                                    Total: {formatCurrency(fromCents(offer.totalPrice), 'EUR')}
                                                                                </div>
                                                                                <div className="small text-muted">
                                                                                    Daily: {formatCurrency(fromCents(offer.dailyPrice), 'EUR')}
                                                                                </div>

                                                                                {offer.bestOffer && (
                                                                                    <div className="small text-success">
                                                                                        Best offer
                                                                                    </div>
                                                                                )}

                                                                                {isSelected && (
                                                                                    <div className="small text-primary fw-semibold mt-1">
                                                                                        Selected
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            );
                                        })}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={3} style={{ height: '100%' }}>
                        <div className="d-flex flex-column gap-3 h-100" style={{ minHeight: 0 }}>
                            {!!searchResult && !searchLoading && (
                                <Card>
                                    <Card.Body className="p-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <div className="fw-semibold">Filters</div>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={clearFilters}
                                                disabled={!hasActiveFilters}
                                            >
                                                Clear
                                            </Button>
                                        </div>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="small mb-1">Brand</Form.Label>
                                            <SmartSearchSelect
                                                isMulti
                                                currentID={selectedBrands}
                                                options={availableBrands}
                                                maxVisibleMultiTags={2}
                                                onChange={(selected) => {
                                                    setSelectedBrands(
                                                        Array.isArray(selected)
                                                            ? selected.map((item) => item.value)
                                                            : []
                                                    );
                                                }}
                                                placeholder="All brands"
                                                noOptionsMessage="No brands found"
                                                loadingMessage="Searching brands..."
                                            />

                                            {selectedBrands.length > 0 && (
                                                <div className="mt-2">
                                                    <div className="small text-muted mb-1">
                                                        Selected brands ({selectedBrands.length})
                                                    </div>

                                                    <div className="d-flex flex-wrap gap-1">
                                                        {selectedBrands.map((brand) => (
                                                            <Badge
                                                                key={brand}
                                                                bg="light"
                                                                text="dark"
                                                                className="border d-inline-flex align-items-center gap-1 px-2 py-1"
                                                            >
                                                                <span>{brand}</span>
                                                                <span
                                                                    role="button"
                                                                    style={{ cursor: 'pointer' }}
                                                                    onClick={() =>
                                                                        setSelectedBrands((prev) => prev.filter((x) => x !== brand))
                                                                    }
                                                                >
                                                                    ×
                                                                </span>
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </Form.Group>

                                        <Form.Group className="mb-2">
                                            <Form.Label className="small mb-1">Min total (€)</Form.Label>
                                            <InputGroup size="sm">
                                                <InputGroup.Text>€</InputGroup.Text>
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={minPrice}
                                                    onChange={(e) => setMinPrice(e.target.value)}
                                                    placeholder={String(fromCents(detectedMinPrice) || 0)}
                                                />
                                            </InputGroup>
                                        </Form.Group>

                                        <Form.Group className="mb-2">
                                            <Form.Label className="small mb-1">Max total (€)</Form.Label>
                                            <InputGroup size="sm">
                                                <InputGroup.Text>€</InputGroup.Text>
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={maxPrice}
                                                    onChange={(e) => setMaxPrice(e.target.value)}
                                                    placeholder={String(fromCents(detectedMaxPrice) || 0)}
                                                />
                                            </InputGroup>
                                        </Form.Group>

                                        <div className="small text-muted mt-2">
                                            {formatCurrency(fromCents(detectedMinPrice), 'EUR')}
                                            {' — '}
                                            {formatCurrency(fromCents(detectedMaxPrice), 'EUR')}
                                        </div>
                                    </Card.Body>
                                </Card>
                            )}
                            <Card>
                                <Card.Body className="p-3">
                                    <div className="fw-semibold mb-2">Request reference</div>

                                    <div className="small d-flex flex-column gap-2">
                                        <div>
                                            <span className="text-muted">Country:</span>{' '}
                                            <span className="fw-semibold">{request?.contactCountry || '—'}</span>
                                        </div>
                                        <div>
                                            <span className="text-muted">Location:</span>{' '}
                                            <span className="fw-semibold">{request?.place || '—'}</span>
                                        </div>
                                        <div>
                                            <span className="text-muted">Start:</span>{' '}
                                            <span className="fw-semibold">
                                                {request?.plannedFrom
                                                    ? new Date(request.plannedFrom).toLocaleString()
                                                    : '—'}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-muted">End:</span>{' '}
                                            <span className="fw-semibold">
                                                {request?.plannedTo
                                                    ? new Date(request.plannedTo).toLocaleString()
                                                    : '—'}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-muted">People:</span>{' '}
                                            <span className="fw-semibold">
                                                {typeof request?.partySize === 'number'
                                                    ? request.partySize
                                                    : '—'}
                                            </span>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    </Col>
                </Row>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    className="btn-custom-success border"
                    onClick={handleConfirm}
                    disabled={!searchResult || !selectedShopMotorcycleId}
                >
                    Confirm
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default MotoSearchModal;